import React, { useEffect, useState, useMemo } from "react";
import {
  PDFViewer,
  Page,
  Text,
  View,
  Document,
} from "@react-pdf/renderer";
import { Spin } from "antd";
import moment from "moment";
import axios from "axios";
import config from "../../config";
import PrintHeaderAccount from "../../Pdf/PrintHeaderAccount";
import {
  PrintFormateNumber,
  parseReportAmount,
} from "../../Pdf/PrintFormateNumber";
import { saleReportStyles } from "../../Pdf/pdfStyles";
import PrintFooter from "../../Pdf/PrintFooter";

const COL = {
  sr: 26,
  invoice: 48,
  dated: 52,
  description: 233,
  cash: 69,
  credit: 69,
  total: 70,
};

const TABLE_WIDTH =
  COL.sr +
  COL.invoice +
  COL.dated +
  COL.description +
  COL.cash +
  COL.credit +
  COL.total;

const resolveReportDate = (raw) => {
  if (!raw || raw === "null" || raw === "undefined") return null;
  const parsed = moment(raw, ["YYYY-MM-DD", config.date_format], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : null;
};

function PrintSaleList() {
  const urlParams = new URLSearchParams(window.location.search);
  const UserID = localStorage.getItem("ID");
  const AccessKey = localStorage.getItem("AccessKey");

  const rawStartDate = urlParams.get("StartDate");
  const rawEndDate = urlParams.get("EndDate");
  const startDate = resolveReportDate(rawStartDate);
  const endDate = resolveReportDate(rawEndDate);

  const [listofSale, setListofSale] = useState([]);
  const [reportMeta, setReportMeta] = useState({});
  const [loading, setLoading] = useState(true);

  const periodLabel = useMemo(() => {
    const effectiveStart = startDate || "2022-01-01";
    const effectiveEnd = endDate || moment().add(2, "days").format("YYYY-MM-DD");
    if (!startDate && !endDate) {
      return "All sales";
    }
    return `${moment(effectiveStart).format(config.date_format)} to ${moment(
      effectiveEnd
    ).format(config.date_format)}`;
  }, [startDate, endDate]);

  useEffect(() => {
    document.title = "Print Sale List";
    if (!UserID || !AccessKey) {
      setLoading(false);
      return;
    }

    // Match ViewSales / Sale/GetSales: empty dates use API defaults (2022-01-01 → today+2).
    const normalizedStart = startDate || "2022-01-01";
    const normalizedEnd =
      endDate || moment().add(2, "days").format("YYYY-MM-DD");

    setLoading(true);
    axios({
      method: "post",
      url: config.base_url + "Sale/GetSales",
      headers: { "Content-Type": "application/json" },
      data: {
        UserID,
        AccessKey,
        StartDate: normalizedStart,
        EndDate: normalizedEnd,
        PageNo: 1,
        PageSize: 20000,
      },
    })
      .then(function (response) {
        if (response.data.status_code == 1) {
          setListofSale(response.data.listofSale || []);
          setReportMeta(response.data);
        } else {
          setListofSale([]);
          setReportMeta({});
        }
      })
      .catch(function () {
        setListofSale([]);
        setReportMeta({});
      })
      .finally(function () {
        setLoading(false);
      });
  }, [UserID, AccessKey, startDate, endDate]);

  const fmt = (val) => PrintFormateNumber(parseReportAmount(val), 0);

  const generateData = () => {
    if (!listofSale.length) return null;

    return listofSale.map((item, index) => (
      <View key={item.ID || index} style={saleReportStyles.row}>
        <View style={[saleReportStyles.td, { width: COL.sr }]}>
          <Text style={saleReportStyles.tdValue}>{index + 1}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: COL.invoice }]}>
          <Text style={saleReportStyles.tdValue}>{item.InvoiceNo ?? "—"}</Text>
        </View>
        <View style={[saleReportStyles.td, { width: COL.dated }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Dated
              ? moment(item.Dated).format(config.date_format)
              : "—"}
          </Text>
        </View>
        <View style={[saleReportStyles.td, { width: COL.description }]}>
          <Text style={saleReportStyles.tdValue}>
            {item.Description || "—"}
          </Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: COL.cash, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmt(item.Cash_Total)}</Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: COL.credit, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmt(item.Credit_Total)}</Text>
        </View>
        <View
          style={[
            saleReportStyles.td,
            { width: COL.total, textAlign: "right" },
          ]}
        >
          <Text style={saleReportStyles.tdValue}>{fmt(item.TotalSales)}</Text>
        </View>
      </View>
    ));
  };

  const MyDocument = () => (
    <Document>
      <Page size="A4" style={saleReportStyles.page}>
        <PrintHeaderAccount compact />
        <View style={saleReportStyles.section}>
          <View style={saleReportStyles.titleBlock}>
            <Text style={saleReportStyles.pagetitle}>Sales List</Text>
            <Text style={saleReportStyles.reportSubtitle}>
              Period: {periodLabel}
            </Text>
            {reportMeta.totalRecords != null ? (
              <Text style={saleReportStyles.reportSubtitle}>
                Invoices: {reportMeta.totalRecords}
              </Text>
            ) : null}
          </View>

          <View style={[saleReportStyles.table, { width: TABLE_WIDTH }]}>
            <View style={saleReportStyles.tableHead}>
              <View style={[saleReportStyles.th, { width: COL.sr }]}>
                <Text style={saleReportStyles.thValue}>Sr.#</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.invoice }]}>
                <Text style={saleReportStyles.thValue}>Invoice</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.dated }]}>
                <Text style={saleReportStyles.thValue}>Dated</Text>
              </View>
              <View style={[saleReportStyles.th, { width: COL.description }]}>
                <Text style={saleReportStyles.thValue}>Description</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.cash, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Cash</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.credit, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Credit</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.total, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Total</Text>
              </View>
            </View>

            {generateData()}

            <View style={saleReportStyles.totalsRow}>
              <View
                style={[
                  saleReportStyles.th,
                  {
                    width: TABLE_WIDTH - COL.cash - COL.credit - COL.total,
                    textAlign: "right",
                  },
                ]}
              >
                <Text style={saleReportStyles.thValue}>Total</Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.cash, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(reportMeta.CashTotal)}
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.credit, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(reportMeta.CreditTotal)}
                </Text>
              </View>
              <View
                style={[
                  saleReportStyles.th,
                  { width: COL.total, textAlign: "right" },
                ]}
              >
                <Text style={saleReportStyles.thValue}>
                  {fmt(reportMeta.TotalSales)}
                </Text>
              </View>
            </View>
          </View>
        </View>
        <PrintFooter fixed />
      </Page>
    </Document>
  );

  return (
    <div style={{ width: "100%", height: "100vh", overflow: "hidden" }}>
      {loading ? (
        <div
          style={{
            height: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Spin />
        </div>
      ) : (
        <PDFViewer width="100%" height="100%">
          <MyDocument />
        </PDFViewer>
      )}
    </div>
  );
}

export default PrintSaleList;
